// Route prefetching — makes navigation feel near-instant.
// ======================================================
// Every page in App.js is React.lazy()'d, so its JS chunk is only
// fetched when the user first navigates there (a brief <RouteLoading/>
// flash on slow connections). This module warms the chunk AHEAD of the
// click: the moment a user hovers (desktop) or touches (mobile) an
// internal link, we kick off the same dynamic import() webpack uses for
// that route. By the time they actually click, the chunk is already in
// the browser cache and the page mounts immediately.
//
// Why this is safe + free:
//   • import() is idempotent + cached — calling it early just moves the
//     network fetch a few hundred ms earlier into idle time.
//   • The importer paths resolve to the SAME modules App.js lazy-loads,
//     so webpack dedupes them to one shared chunk (no extra bytes).
//   • Everything is best-effort and swallows errors.

// Map of route path → the page's chunk importer. Mirrors App.js's lazy
// imports for the high-traffic, user-reachable routes. Unlisted routes
// simply don't prefetch (harmless). `../pages/X` from src/lib resolves
// to the same module as `./pages/X` from src, so chunks are shared.
const ROUTE_IMPORTERS = {
  "/foryou": () => import("../pages/ForYou"),
  "/bible": () => import("../pages/Bible"),
  "/apocrypha": () => import("../pages/Apocrypha"),
  "/dictionary": () => import("../pages/Dictionary"),
  "/concordance": () => import("../pages/Concordance"),
  "/crossword": () => import("../pages/Crossword"),
  "/puzzles": () => import("../pages/Crossword"),
  "/precepts": () => import("../pages/Precepts"),
  "/sermons": () => import("../pages/Sermons"),
  "/news": () => import("../pages/News"),
  "/lessons": () => import("../pages/Lessons"),
  "/choir": () => import("../pages/Choir"),
  "/gospel-music": () => import("../pages/GospelMusic"),
  "/music-videos": () => import("../pages/MusicVideos"),
  "/comedy": () => import("../pages/Comedy"),
  "/comedy/foundational": () => import("../pages/FoundationalComedy"),
  "/christian-rap": () => import("../pages/ChristianRap"),
  "/my-mix": () => import("../pages/MyMix"),
  "/community-shorts": () => import("../pages/CommunityShorts"),
  "/videos": () => import("../pages/Videos"),
  "/kids": () => import("../pages/Kids"),
  "/movies": () => import("../pages/Movies"),
  "/movies/black-american": () => import("../pages/MoviesBlackAmerican"),
  "/memorial": () => import("../pages/Memorial"),
  "/churches": () => import("../pages/Churches"),
  "/storybook": () => import("../pages/Storybook"),
  "/live": () => import("../pages/LivePage"),
  "/wall": () => import("../pages/Wall"),
  "/search": () => import("../pages/Search"),
  "/bookmarks": () => import("../pages/Bookmarks"),
  "/history": () => import("../pages/History"),
  "/recipes": () => import("../pages/Recipes"),
  "/study-group": () => import("../pages/StudyGroup"),
  "/rooms": () => import("../pages/Rooms"),
  "/circles": () => import("../pages/Circles"),
  "/messages": () => import("../pages/Messages"),
  "/friends": () => import("../pages/Friends"),
  "/holyletters": () => import("../pages/HolyLetters"),
  "/my-lineup": () => import("../pages/MyLineup"),
  "/settings": () => import("../pages/Settings"),
};

// Resolve which importer (if any) handles a given pathname.
function importerForPath(pathname) {
  if (!pathname) return null;
  const p = pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname;
  if (ROUTE_IMPORTERS[p]) return ROUTE_IMPORTERS[p];
  // Chapter readers are their own (heavier) chunks.
  if (/^\/bible\/[^/]+\/[^/]+/.test(p)) return () => import("../pages/Reader");
  if (/^\/apocrypha\/[^/]+\/[^/]+/.test(p)) return () => import("../pages/ApocryphaReader");
  if (/^\/recipes\/[^/]+/.test(p)) return () => import("../pages/RecipeDetail");
  // Longest-prefix fallback (e.g. /playlists/:slug → /playlists).
  let best = null;
  for (const key of Object.keys(ROUTE_IMPORTERS)) {
    if (key !== "/" && p.startsWith(key + "/") && (!best || key.length > best.length)) {
      best = key;
    }
  }
  return best ? ROUTE_IMPORTERS[best] : null;
}

const _prefetched = new Set();

export function prefetchByHref(href) {
  try {
    if (!href) return;
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    if (_prefetched.has(url.pathname)) return;
    const imp = importerForPath(url.pathname);
    if (!imp) return;
    _prefetched.add(url.pathname);
    imp().catch(() => {});
  } catch (_) {
    /* malformed href — ignore */
  }
}

// Install ONE delegated listener set for the whole app. Hovering /
// focusing / touching any internal <a> warms its route chunk. Returns
// a teardown fn. Idempotent — safe to call from a mounting effect.
export function installRoutePrefetch() {
  if (typeof document === "undefined" || document.__hwPrefetchInstalled) {
    return () => {};
  }
  document.__hwPrefetchInstalled = true;
  let scheduled = null;
  const handler = (e) => {
    const target = e.target;
    const a = target && target.closest ? target.closest('a[href^="/"]') : null;
    if (!a) return;
    const href = a.getAttribute("href");
    if (!href || href.startsWith("//")) return;
    // Coalesce rapid mouse-sweeps into one rAF so we don't thrash.
    if (scheduled) cancelAnimationFrame(scheduled);
    scheduled = requestAnimationFrame(() => prefetchByHref(href));
  };
  document.addEventListener("pointerover", handler, { passive: true });
  document.addEventListener("touchstart", handler, { passive: true });
  document.addEventListener("focusin", handler, { passive: true });
  return () => {
    document.removeEventListener("pointerover", handler);
    document.removeEventListener("touchstart", handler);
    document.removeEventListener("focusin", handler);
    document.__hwPrefetchInstalled = false;
  };
}
